import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { QuoteModel } from '@data-layer/models/lcl';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { filter, finalize, switchMap, take } from 'rxjs/operators';
import { EntitiesRepository } from '@data-layer/repositories';
import { CompaniesService } from '@services/companies.service';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { ApplicationModeService } from '@services/application-mode.service';

@Injectable({ providedIn: 'root' })
export class LclQuoteService {
  public updateLclQuote$: Subject<boolean> = new Subject<boolean>();

  private entitiesToCleanInProcess: boolean;
  private entitiesToClean$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

  constructor(
    private companiesService: CompaniesService,
    private entitiesRepository: EntitiesRepository,
    private applicationModeService: ApplicationModeService,
  ) {}

  public setEntitiesToClean(ids: number[]): void {
    this.entitiesToClean$.next(ids);
  }

  public handleEntities(
    quote: QuoteModel,
    entity: EntityModel,
    isChangeRole: boolean,
  ): { consignee: Observable<EntityModel>; notifyParty: Observable<EntityModel>; shipper: Observable<EntityModel> } {
    // there are 7 cases
    // 1. Consignee exists & Shipper not -> and needs to be moved to Shipper
    // 2. Consignee exists & Shipper too -> and needs to be moved to Shipper & Shipper moved to Consignee
    // 3. Shipper exists & Consignee not -> and needs to be moved to Consignee
    // 4. Shipper exists & Consignee too -> and needs to be moved to Consignee & Consignee moved to Shipper
    // 5. Consignee party -> only create/update it
    // 6. Notify party -> only create/update it
    // 7. Shipper party -> only create/update it

    let res: { consignee: Observable<EntityModel>; notifyParty: Observable<EntityModel>; shipper: Observable<EntityModel> } = {
      consignee: of(quote.consignee),
      notifyParty: of(quote.notifyParty),
      shipper: of(quote.shipper),
    };

    let companyId: number = this.companiesService.getCompany()?.id;

    if (this.isActiveSupportMode) {
      companyId = quote?.companyId;
    }

    if (!isChangeRole) {
      // case 5, 6, 7
      switch (entity.entityCode) {
        case enumEntityModelCode.CONSIGNEE:
          res = { consignee: this.сreateOrUpdateEntity(entity, companyId), notifyParty: of(quote.notifyParty), shipper: of(quote.shipper) };
          break;
        case enumEntityModelCode.NOTIFY_PARTY:
          res = { consignee: of(quote.consignee), notifyParty: this.сreateOrUpdateEntity(entity, companyId), shipper: of(quote.shipper) };
          break;
        case enumEntityModelCode.SHIPPER:
          res = {
            consignee: of(quote.consignee),
            notifyParty: of(quote.notifyParty),
            shipper: this.сreateOrUpdateEntity(entity, companyId),
          };
          break;
      }
    } else {
      // case 1
      if (entity.entityCode === enumEntityModelCode.CONSIGNEE && !quote.consignee) {
        res = {
          consignee: this.сreateOrUpdateEntity(entity, companyId, true),
          notifyParty: of(quote.notifyParty),
          shipper: of(null),
        };
      }
      // case 2
      if (entity.entityCode === enumEntityModelCode.CONSIGNEE && !!quote.consignee) {
        res = {
          consignee: this.сreateOrUpdateEntity({ ...entity, id: null }, companyId, true),
          notifyParty: of(quote.notifyParty),
          shipper: this.сreateOrUpdateEntity({ ...quote.consignee, entityCode: enumEntityModelCode.SHIPPER, id: null }, companyId, true),
        };
      }
      // case 3
      if (entity.entityCode === enumEntityModelCode.SHIPPER && !quote.shipper) {
        res = {
          consignee: of(null),
          notifyParty: of(quote.notifyParty),
          shipper: this.сreateOrUpdateEntity(entity, companyId, true),
        };
      }
      // case 4
      if (entity.entityCode === enumEntityModelCode.SHIPPER && !!quote.shipper) {
        res = {
          consignee: this.сreateOrUpdateEntity({ ...quote.shipper, entityCode: enumEntityModelCode.CONSIGNEE, id: null }, companyId, true),
          notifyParty: of(quote.notifyParty),
          shipper: this.сreateOrUpdateEntity({ ...entity, id: null }, companyId, true),
        };
      }
    }

    return res;
  }

  public cleanOldEntities(): Observable<EntityModel[]> {
    const apiCalls: any[] = [];

    return this.entitiesToClean$.pipe(
      take(1),
      filter(() => !this.entitiesToCleanInProcess),
      switchMap((ids: number[]) => {
        const uniqueIds: number[] = [...new Set(ids)];

        (uniqueIds || []).forEach((item: number) => {
          apiCalls.push(this.entitiesRepository[this.isActiveSupportMode ? 'managementDelete' : 'deleteEntity'](item));
        });

        this.entitiesToCleanInProcess = true;

        return !!apiCalls.length ? forkJoin(apiCalls) : of(null);
      }),
      finalize(() => {
        this.entitiesToCleanInProcess = false;
        this.entitiesToClean$.next([]);
      }),
    );
  }

  private сreateOrUpdateEntity(entity: EntityModel, companyId: number, isRecreate?: boolean): Observable<EntityModel> {
    return !!entity?.id && !isRecreate
      ? this.entitiesRepository[this.isActiveSupportMode ? 'managementUpdate' : 'updateEntity'](entity.id, entity)
      : this.entitiesRepository[this.isActiveSupportMode ? 'managementCreate' : 'createEntity'](companyId, entity);
  }

  private get isActiveSupportMode(): boolean {
    return this.applicationModeService.activeApplicationMode === enumApplicationMode.SUPPORT;
  }
}
